<script setup lang="ts">
import StyledDropZone from "@/components/StyledDropZone.vue";
import useAccount from "@/composables/useAccount.ts";
import { Company } from "@/modules/companies/interfaces/company.ts";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { reactive } from "vue";
import { useToast } from "vue-toastification";

interface Props {
  companies: Company[];
}

interface Emits {
  (e: "actionModalInviteUser"): void;
}

const props = defineProps<Props>();
const emits = defineEmits<Emits>();

const { inviteMassiveUserAsync, inviteMassiveUserLoading } = useAccount();
const toast = useToast();

interface Form {
  file: File | null;
  companyId: number | null;
}

const form: Form = reactive({
  file: null,
  companyId: null,
});

const rules = {
  companyId: {
    required: helpers.withMessage("La empresa es requerida", required),
  },
  file: {
    required: helpers.withMessage("El archivo es requerido", required),
  },
};

const v$ = useVuelidate(rules, form);

const handleInviteMassiveUser = async () => {
  for (const field of v$.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message);
      return;
    }
  }

  if (!form.companyId || !form.file) {
    return;
  }

  await inviteMassiveUserAsync({
    params: {
      file: form.file,
      companyId: form.companyId,
    },
    config: {
      onSuccessToast: "Invitaciones enviadas correctamente",
      onErrorToast: "Error al enviar invitaciones",
      raiseToast: true,
    },
  });

  emits("actionModalInviteUser");
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleInviteMassiveUser">
    <!-- Empresa -->
    <div class="flex flex-col gap-1">
      <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">
        Empresa <span class="text-red-500">*</span>
      </label>
      <div class="relative">
        <select
          v-model="form.companyId"
          class="w-full text-sm rounded-lg px-3.5 py-2.5 border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#021F58]/25 focus:border-[#021F58] appearance-none pr-9 transition-all text-gray-700"
        >
          <option :value="null" disabled>Selecciona una empresa</option>
          <option
            v-for="company in companies"
            :key="company.id"
            :value="company.id"
          >{{ company.name }}</option>
        </select>
        <span class="pointer-events-none absolute inset-y-0 right-3 flex items-center">
          <span class="material-symbols-outlined text-gray-400 text-[18px]">expand_more</span>
        </span>
      </div>
    </div>

    <!-- Plantilla info -->
    <div class="flex items-start gap-2.5 rounded-xl bg-blue-50 border border-blue-100 px-4 py-3">
      <span class="material-symbols-outlined text-blue-400 text-base flex-shrink-0 mt-0.5">info</span>
      <p class="text-sm text-blue-700 leading-relaxed">
        Descarga la
        <a
          href="https://enlacebooking.s3.us-east-2.amazonaws.com/public/massive+invite+template.csv"
          download
          class="font-semibold underline"
        >plantilla de invitaciones</a>
        y llénala con los correos de los usuarios a invitar.
      </p>
    </div>

    <!-- Drop zone -->
    <div class="flex flex-col gap-1">
      <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">
        Archivo CSV <span class="text-red-500">*</span>
      </label>
      <StyledDropZone
        v-model="form.file"
        label="Documento"
        :accept-types="['text/csv']"
      />
    </div>

    <!-- Submit -->
    <div class="flex justify-end pt-1">
      <button
        type="submit"
        :disabled="inviteMassiveUserLoading"
        class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#021F58] text-white text-sm font-semibold hover:bg-[#1C3F7A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
      >
        <div v-if="inviteMassiveUserLoading" class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
        <span v-else class="material-symbols-outlined text-base">group_add</span>
        Enviar invitaciones
      </button>
    </div>
  </form>
</template>
